import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback } from 'react';

import SEO from '../views/components/SEO';
import MainLayout from '../views/layouts/MainLayout';
import Image from '../views/components/Image';
import useFirebase from '../services/firebase/useFirebase';
import { getCurrentStore, synchronizeStoreData } from '../services/auth';
import './index.styl';

// Clé de stockage pour la dernière synchronisation sur la page d'accueil
const HOME_SYNC_KEY = 'brico_home_sync_timestamp';

// Intervalle minimum entre les synchronisations (1 heure)
const HOME_SYNC_INTERVAL = 60 * 60 * 1000;

const IndexPage = ({ data, location }) => {
    const { alu, pvc, wood } = data;
    const [syncStatus, setSyncStatus] = useState(null);
    const firebase = useFirebase();
    const { id } = getCurrentStore();

    // Vérifier si une synchronisation est nécessaire
    const shouldSync = useCallback(() => {
        // Récupérer le timestamp de la dernière synchronisation
        let lastSyncTimestamp;
        try {
            lastSyncTimestamp = localStorage.getItem(HOME_SYNC_KEY);
        } catch (error) {
            return false;
        }

        if (!lastSyncTimestamp) {
            return true; // Jamais synchronisé
        }

        const now = Date.now();
        const lastSync = parseInt(lastSyncTimestamp, 10);

        // Vérifier si l'intervalle minimum est passé
        return now - lastSync > HOME_SYNC_INTERVAL;
    }, []);

    // Marquer la synchronisation comme effectuée
    const markSyncComplete = useCallback(() => {
        try {
            localStorage.setItem(HOME_SYNC_KEY, Date.now().toString());
        } catch (error) {
            // Synchronisation remains optional when storage is unavailable.
        }
    }, []);

    // Synchroniser les données du magasin au chargement de la page d'accueil
    useEffect(() => {
        const syncStoreData = async () => {
            if (!firebase || !id || !shouldSync()) {
                return;
            }

            try {
                // Synchroniser les données avec Firebase
                const storeData = await synchronizeStoreData(firebase, id);

                if (storeData) {
                    setSyncStatus({
                        success: true,
                        message: 'Données synchronisées avec succès',
                        bassin: storeData.bassin,
                    });

                    // Marquer la synchronisation comme effectuée
                    markSyncComplete();
                }
            } catch (error) {
                console.error('Error syncing store data on home page:', error);
            }
        };

        syncStoreData();
    }, [firebase, id, markSyncComplete, shouldSync]);

    const sections = [
        {
            className: 'color-pvc',
            image: pvc.childImageSharp,
            label: 'PVC',
            url: 'pvc',
        },
        {
            className: 'color-alu',
            image: alu.childImageSharp,
            label: 'Aluminium',
            url: 'aluminium',
        },
        {
            className: 'color-wood',
            image: wood.childImageSharp,
            label: 'Bois',
            url: 'bois',
        },
    ];

    return (
        <MainLayout
            headerOptions={{
                headerTitle: 'Choisissez la matière de vos fenêtres',
            }}
        >
            <SEO
                title='Brico App'
                location={location}
                description='Brico App'
            />
            <div className='content home'>
                {sections.map((section) => (
                    <div
                        key={`home-section-${section.label}`}
                        className='home-section'
                    >
                        <Image
                            alt={`home section ${section.label}`}
                            {...section.image}
                        />
                        <Link
                            to={`/window-type/${section.url}/`}
                            className={`section-button ${section.className}`}
                        >
                            Fenêtres <span>{section.label}</span>
                        </Link>
                    </div>
                ))}

                {syncStatus && syncStatus.success && (
                    <div className='sync-success-indicator'>
                        <span className='sync-dot' />
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

IndexPage.propTypes = {
    data: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
};

export default IndexPage;

export const query = graphql`
    query homePictures {
        wood: file(relativePath: { eq: "wood.png" }) {
            childImageSharp {
                gatsbyImageData(width: 640, layout: CONSTRAINED)
            }
        }
        alu: file(relativePath: { eq: "alu.png" }) {
            childImageSharp {
                gatsbyImageData(width: 640, layout: CONSTRAINED)
            }
        }
        pvc: file(relativePath: { eq: "pvc.png" }) {
            childImageSharp {
                gatsbyImageData(width: 640, layout: CONSTRAINED)
            }
        }
    }
`;
